import * as THREE from "three";
import { VFX } from "../../../../../art/vfx.js";
import { SkinFx } from "../skins/kit/fx";
import type { AmbienceSpec } from "../skins/kit/types";
import { reducedVfx } from "../../ui/settings";
import { groundAt } from "../terrain";
import type { TowerView } from "./towerView";

/**
 * A premium fortress's set-piece (fortress v2, 2026-10-19; owner: "the skins need dramatic effects as they're paid
 * for … unique aspects and animations"): one signature effect per skin on top of its model's clips, through the pooled
 * VFX, in a level only.
 *
 *   lava       the Dragonlord: the molten seams spit — a gout of embers and a heat bloom off a random seam hot-spot
 *              (`glowtw*`) or lava fall (`water*`) every 0.6–1.2 s
 *   moonbeam   the Moonstone Keep: every 3–5 s a shaft of moonlight falls from the moon (`condgem0`) to the courtyard,
 *              pale motes drifting down it and a cold ring where it lands
 *   starfall   the Starfall Citadel: shooting stars streak in out of the sky every 1.2–2.4 s and land on a spire star
 *              (`finial*`) in a burst of sparkles; now and then two arrive together
 */
interface Shot { from: THREE.Vector3; to: THREE.Vector3; t: number; len: number }

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();

export class FortressAmbience {
  private next: number;
  private readonly shots: Shot[] = [];
  private readonly spots: THREE.Object3D[];
  private readonly tip: THREE.Object3D | null;

  private constructor(private readonly spec: AmbienceSpec, private readonly v: TowerView) {
    this.next = spec.every[0] * Math.random();
    const want = spec.kind === "lava" ? ["glowtw", "water"] : spec.kind === "starfall" ? ["finial"] : [];
    this.spots = want.flatMap((p) => parts(v, p));
    this.tip = parts(v, "condgem")[0] ?? null;
  }

  /** The set-piece of a fortress view's skin, or null for a fortress without one. */
  static of(v: TowerView): FortressAmbience | null {
    const spec = SkinFx.fortressKit(v.model.name)?.ambience;
    return spec ? new FortressAmbience(spec, v) : null;
  }

  update(dt: number, fx: VFX | null): void {
    if (!fx || reducedVfx()) return;
    this.step(dt, fx);
    if ((this.next -= dt) > 0) return;
    const [lo, hi] = this.spec.every;
    this.next = lo + Math.random() * (hi - lo);
    if (this.spec.kind === "lava") this.lava(fx);
    else if (this.spec.kind === "moonbeam") this.moonbeam(fx);
    else { this.shoot(); if (Math.random() < 0.25) this.shoot(); }
  }

  private lava(fx: VFX): void {
    const s = this.spots.length ? this.spots[Math.floor(Math.random() * this.spots.length)] : null;
    if (!s || s.scale.x < 0.05) return;
    const at = s.getWorldPosition(_a);
    fx.embers(at, 5, { color: this.spec.colour, radius: 0.12, life: 0.9, size: 0.07 });
    fx.glowPulse(at, { size: 0.5, life: 0.4, color: this.spec.colour });
    if (Math.random() < 0.3) fx.sparks(at, 4, { color: this.spec.colour, speed: 1.2, up: 1.6, life: 0.5, size: 0.05 });
  }

  private moonbeam(fx: VFX): void {
    if (!this.tip) return;
    const top = this.tip.getWorldPosition(_a).clone();
    this.v.box.getCenter(_b);
    const floor = _b.set(_b.x, groundAt(_b.x, _b.z) + 0.25, _b.z).clone();
    fx.beam(top, floor, { life: 1.4, width: 0.35, color: this.spec.hex });
    fx.ring(floor, { size: 1.6, life: 1.0, color: this.spec.colour, flat: true, bright: 1.2 });
    for (let k = 0; k < 5; k++) fx.sparkles(_a.lerpVectors(top, floor, Math.random()), 1, { color: this.spec.colour, radius: 0.12, rise: -0.4, life: 1.2, size: 0.09 });
  }

  private shoot(): void {
    const to = this.spots.length ? this.spots[Math.floor(Math.random() * this.spots.length)] : this.tip;
    if (!to) return;
    const end = to.getWorldPosition(new THREE.Vector3());
    const a = Math.random() * Math.PI * 2;
    const from = end.clone().add(new THREE.Vector3(Math.cos(a) * 3.5, 3.5 + Math.random() * 1.5, Math.sin(a) * 3.5));
    this.shots.push({ from, to: end, t: 0, len: 0.55 + Math.random() * 0.25 });
  }

  /** Moves the shooting stars in flight: a sparkle trail each frame, a burst where they land. */
  private step(dt: number, fx: VFX): void {
    for (let i = this.shots.length - 1; i >= 0; i--) {
      const s = this.shots[i];
      s.t += dt;
      const k = Math.min(1, s.t / s.len);
      const at = _a.lerpVectors(s.from, s.to, k * k);          // accelerating in
      fx.sparkles(at, 2, { color: this.spec.colour, radius: 0.04, rise: 0, life: 0.35, size: 0.11 });
      if (k >= 1) {
        fx.flash(s.to, 0.5, [1, 0.95, 1]);
        fx.sparkles(s.to, 8, { color: this.spec.colour, radius: 0.25, rise: 0.3, life: 0.7, size: 0.1 });
        fx.ring(s.to, { size: 0.6, life: 0.4, color: this.spec.colour, flat: false, bright: 1.6 });
        this.shots.splice(i, 1);
      }
    }
  }
}

/** The model's numbered parts (`finial0` …) under either spelling of the node name (see hqNode). */
function parts(v: TowerView, part: string): THREE.Object3D[] {
  const re = new RegExp(`^${v.model.name}\\.?${part}\\d+$`);
  const out: THREE.Object3D[] = [];
  v.model.storeys.traverse((o) => { if (re.test(o.name)) out.push(o); });
  return out;
}
